import type { IntlShape } from 'react-intl';
import { AlertType } from '@alltrails/types/Alert';

const alertTypeStrings = (intl: IntlShape): Record<AlertType, { label: string; info: string }> => ({
  [AlertType.WARNING]: {
    label: intl.formatMessage({ defaultMessage: 'Caution' }),
    info: intl.formatMessage({
      defaultMessage:
        'A caution alert is less severe than a danger alert and should be used to communicate a possibly hazardous situation to your visitors.'
    })
  },
  [AlertType.INFORMATION]: {
    label: intl.formatMessage({ defaultMessage: 'Information' }),
    info: intl.formatMessage({
      defaultMessage:
        "An information alert should be used to communicate something that may affect your visitor's plans but is not hazardous. An information alert is the most general alert and may be used if you are unsure of what alert type to choose."
    })
  },
  [AlertType.CLOSURE]: {
    label: intl.formatMessage({ defaultMessage: 'Closure' }),
    info: intl.formatMessage({
      defaultMessage:
        'A closure alert may be attached to a trail that is closed due to trail impacts, trail/park construction, trail maintenance, seasonal closures, weather, etc.'
    })
  },
  [AlertType.DANGER]: {
    label: intl.formatMessage({ defaultMessage: 'Danger' }),
    info: intl.formatMessage({
      defaultMessage:
        'A danger alert can be used to communicate an imminent hazard that a visitor could encounter.  This type of alert may be attached to trails that have sustained heavy damage from weather or fire, trails with dangerous wildlife, or any other instance where you would want to warn visitors of something that could affect their activity and safety.'
    })
  }
});

const defaultAlertType = (intl: IntlShape): { value: AlertType; label: string } => ({
  value: AlertType.WARNING,
  label: alertTypeLabel(AlertType.WARNING, intl)
});

const alertTypeLabel = (alertType: AlertType, intl: IntlShape) => {
  const strings = alertTypeStrings(intl)[alertType];
  return strings ? strings.label : '';
};

const alertTypeInfo = (alertType: AlertType, intl: IntlShape) => {
  const strings = alertTypeStrings(intl)[alertType];
  return strings ? strings.info : '';
};

const alertTypeOptions = (intl: IntlShape) =>
  [AlertType.WARNING, AlertType.CLOSURE, AlertType.INFORMATION, AlertType.DANGER].map((alertType: AlertType) => ({
    label: alertTypeLabel(alertType, intl),
    value: alertType
  }));

export { defaultAlertType, alertTypeLabel, alertTypeInfo, alertTypeOptions };
